import { ReactNode, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import './course-enrichment';
import App from './App';
import AccountDashboard from './AccountDashboard';
import AccessibilityTools from './AccessibilityTools';
import AgeGate from './AgeGate';
import AptenvoHomePage from './AptenvoHomePage';
import AuthenticatedAccountPrefill from './AuthenticatedAccountPrefill';
import ContactPage from './ContactPage';
import DigitalSupplyConsent from './DigitalSupplyConsent';
import EnrolmentTargetNotice from './EnrolmentTargetNotice';
import { EnhancedAboutPage, HelpCentrePage } from './EnhancedPages';
import {
  AcceptableUsePolicyPage,
  PrivacyPolicyPage,
  RefundsPolicyPage,
  TermsOfUsePage,
} from './LegalPolicies';
import PublicSiteHeader from './PublicSiteHeader';
import {
  DetailedDeliveryPage,
  DetailedIndividualsPage,
  DetailedOrganisationsPage,
} from './ServiceInformationPages';
import { AccessibilityPolicyPage, ComplaintsPolicyPage, SiteMapPage } from './TrustPages';

const wordmarkStyle = {
  color: '#2563eb',
  fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
  fontSize: '1.42rem',
  fontWeight: 800,
  letterSpacing: '-0.035em',
  lineHeight: 1,
  whiteSpace: 'nowrap' as const,
};

function EnhancedFooter() {
  return <>
    <footer className="footer"><div className="footer-grid">
      <div className="footer-brand"><span style={{ ...wordmarkStyle, color: '#4f7cff', fontSize: '1.65rem' }}>Sousa Murray eLearning</span><p>Adult online training sold and supported by JA Group Services Ltd through Sousa Murray eLearning.</p></div>
      <div><h3>Explore</h3><Link to="/learning-library">Learning Library</Link><Link to="/learning-library/courses">Learning Library courses</Link><Link to="/professional-training">Professional Training</Link><Link to="/courses">Professional course catalogue</Link><Link to="/plans">Learning Library plans</Link><Link to="/organisations">For organisations</Link><Link to="/lms/dashboard">My Sousa Murray eLearning</Link><Link to="/sitemap">Site map</Link></div>
      <div><h3>Help</h3><Link to="/support">Help Centre</Link><Link to="/contact">Contact Sousa Murray eLearning</Link><Link to="/accessibility">Accessibility</Link><Link to="/complaints">Complaints</Link><Link to="/how-courses-are-delivered">How course access works</Link></div>
      <div><h3>Legal</h3><Link to="/terms">Terms of Use</Link><Link to="/privacy">Privacy Policy</Link><Link to="/refunds">Refunds Policy</Link><Link to="/acceptable-use">Acceptable Use Policy</Link><Link to="/cookies">Cookie notice</Link></div>
    </div></footer>
    <div className="corporate-disclosure"><div><strong>Sousa Murray eLearning is a trading division of JA Group Services Ltd.</strong><span>Registered in England and Wales. Company number 16314179. ICO registration ZB877370. Customers and learners must be aged 18 or over.</span></div><span>© {new Date().getFullYear()} JA Group Services Ltd.</span></div>
  </>;
}

function EnhancedLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  useEffect(() => window.scrollTo({ top: 0, behavior: 'auto' }), [location.pathname]);
  return <><PublicSiteHeader />{children}<EnhancedFooter /></>;
}

function WordingEnhancer() {
  useEffect(() => {
    const replacements = new Map([
      ['Browse all 101 courses', 'Explore the course catalogue'],
      ['101 catalogue items', '101 available courses'],
      ['Terms and conditions', 'Terms of Use'],
      ['Privacy notice', 'Privacy Policy'],
      ['Refund policy', 'Refunds Policy'],
    ]);

    const appendLink = (column: HTMLElement, href: string, text: string) => {
      if (column.querySelector(`a[href="${href}"]`)) return;
      const link = document.createElement('a');
      link.href = href;
      link.textContent = text;
      column.append(link);
    };

    const update = () => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let node: Node | null;
      while ((node = walker.nextNode())) {
        const value = node.nodeValue?.trim();
        if (value && replacements.has(value)) node.nodeValue = node.nodeValue?.replace(value, replacements.get(value) ?? value) ?? null;
      }

      document.querySelectorAll<HTMLAnchorElement>('a[href="/business"]').forEach((anchor) => anchor.setAttribute('href', '/organisations'));
      document.querySelectorAll<HTMLAnchorElement>('a[href="/providers"]').forEach((anchor) => anchor.setAttribute('href', '/how-courses-are-delivered'));

      const supportColumn = document.querySelector<HTMLElement>('.footer-grid > div:nth-child(3)');
      if (supportColumn) {
        appendLink(supportColumn, '/contact', 'Contact Sousa Murray eLearning');
        appendLink(supportColumn, '/accessibility', 'Accessibility');
        appendLink(supportColumn, '/complaints', 'Complaints');
      }

      const legalColumn = document.querySelector<HTMLElement>('.footer-grid > div:nth-child(4)');
      if (legalColumn) appendLink(legalColumn, '/acceptable-use', 'Acceptable Use Policy');

      const exploreColumn = document.querySelector<HTMLElement>('.footer-grid > div:nth-child(2)');
      if (exploreColumn) appendLink(exploreColumn, '/sitemap', 'Site map');
    };

    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return null;
}

export default function AppShell() {
  const location = useLocation();
  let enhancedPage: ReactNode = null;
  if (location.pathname === '/') enhancedPage = <AptenvoHomePage />;
  else if (location.pathname === '/about') enhancedPage = <EnhancedAboutPage />;
  else if (location.pathname === '/support' || location.pathname === '/help-centre') enhancedPage = <HelpCentrePage />;
  else if (location.pathname === '/account') enhancedPage = <AccountDashboard />;
  else if (location.pathname === '/individuals') enhancedPage = <><DetailedIndividualsPage /><EnrolmentTargetNotice /></>;
  else if (location.pathname === '/organisations' || location.pathname === '/business') enhancedPage = <><DetailedOrganisationsPage /><EnrolmentTargetNotice /></>;
  else if (location.pathname === '/how-courses-are-delivered' || location.pathname === '/providers') enhancedPage = <><DetailedDeliveryPage /><EnrolmentTargetNotice /></>;
  else if (location.pathname === '/contact') enhancedPage = <ContactPage />;
  else if (location.pathname === '/terms' || location.pathname === '/terms-of-use') enhancedPage = <TermsOfUsePage />;
  else if (location.pathname === '/privacy' || location.pathname === '/privacy-policy') enhancedPage = <PrivacyPolicyPage />;
  else if (location.pathname === '/refunds' || location.pathname === '/refund-policy') enhancedPage = <RefundsPolicyPage />;
  else if (location.pathname === '/acceptable-use' || location.pathname === '/acceptable-use-policy' || location.pathname === '/aup') enhancedPage = <AcceptableUsePolicyPage />;
  else if (location.pathname === '/accessibility' || location.pathname === '/accessibility-policy') enhancedPage = <AccessibilityPolicyPage />;
  else if (location.pathname === '/complaints' || location.pathname === '/complaints-policy') enhancedPage = <ComplaintsPolicyPage />;
  else if (location.pathname === '/sitemap' || location.pathname === '/site-map') enhancedPage = <SiteMapPage />;

  return <>
    <AgeGate />
    <WordingEnhancer />
    <AuthenticatedAccountPrefill />
    <DigitalSupplyConsent />
    <AccessibilityTools />
    {enhancedPage ? <EnhancedLayout>{enhancedPage}</EnhancedLayout> : <App />}
  </>;
}
